import { useState, useMemo } from 'react';
import type { Arealplan } from '@origo/arealplan';
import { type PlanFilters, type SortOption, emptyFilters, sortOptions } from './types';
import { applyFilters, sortPlans, hasActiveFilters, countActiveFilters } from './filterUtils';
import type { PlanMetadata } from '../admin/services/PlanMetadataService';

export interface UsePlanFilterOptions {
    plans: Arealplan[];
    sectionCounts?: Map<string, number>;
    planMetadata?: Map<string, PlanMetadata>;
}

export interface UsePlanFilterReturn {
    filters: PlanFilters;
    setFilters: (f: PlanFilters) => void;
    updateFilters: (patch: Partial<PlanFilters>) => void;
    sortOption: SortOption;
    setSortOption: (o: SortOption) => void;
    filteredPlans: Arealplan[];
    activeFilterCount: number;
    hasActive: boolean;
    resetFilters: () => void;
}

export function usePlanFilter({ plans, sectionCounts, planMetadata }: UsePlanFilterOptions): UsePlanFilterReturn {
    const [filters, setFilters] = useState<PlanFilters>(emptyFilters);
    const [sortOption, setSortOption] = useState<SortOption>(sortOptions[0]);

    const updateFilters = (patch: Partial<PlanFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    };

    const resetFilters = () => setFilters(emptyFilters);

    const filteredPlans = useMemo(
        () => sortPlans(applyFilters(plans, filters, sectionCounts, planMetadata), sortOption),
        [plans, filters, sectionCounts, planMetadata, sortOption]
    );

    return {
        filters,
        setFilters,
        updateFilters,
        sortOption,
        setSortOption,
        filteredPlans,
        activeFilterCount: countActiveFilters(filters),
        hasActive: hasActiveFilters(filters),
        resetFilters,
    };
}
